import { useState, useEffect, useRef } from "react";
import axios from "axios";

const DIAS_OPS = [
    { key: 15, label: "15 días" },
    { key: 30, label: "30 días" },
];

// Series en orden de apilado (abajo → arriba) y colores categóricos
const SERIES = [
    { key: "Aprobado",    label: "Aprobados",    color: "var(--verde-esmeralda)" },
    { key: "Borrador",    label: "Borradores",   color: "var(--ambar-600)" },
    { key: "Pausado",     label: "Pausados",     color: "var(--color-text-secondary)" },
    { key: "Desaprobado", label: "Desaprobados", color: "var(--rojo-600)" },
];

function fmtFecha(iso) {
    if (!iso) return "";
    const [, m, d] = iso.split("-");
    const meses = ["ene","feb","mar","abr","may","jun","jul","ago","sep","oct","nov","dic"];
    return `${Number(d)} ${meses[Number(m) - 1]}`;
}

function fmtFechaCompleta(iso) {
    if (!iso) return "";
    const [y, m, d] = iso.split("-");
    const meses = ["enero","febrero","marzo","abril","mayo","junio","julio","agosto","septiembre","octubre","noviembre","diciembre"];
    return `${Number(d)} de ${meses[Number(m) - 1]} de ${y}`;
}

// ── Gráfica de barras apiladas SVG ───────────────────────────────────────────
function StackedBarChart({ serie, dias }) {
    const svgRef = useRef(null);
    const [tooltip, setTooltip] = useState(null);

    const W = 1000, H = 240, PAD_L = 36, PAD_R = 12, PAD_T = 16, PAD_B = 36;
    const AREA_W = W - PAD_L - PAD_R;
    const AREA_H = H - PAD_T - PAD_B;

    const maxTotal  = Math.max(...serie.map(d => SERIES.reduce((s, k) => s + (d[k.key] || 0), 0)), 1);
    const gridLines = 4;
    const barWidth  = AREA_W / serie.length;
    const barPad    = barWidth * 0.2;
    const barActual = barWidth - barPad * 2;
    const labelCada = dias <= 15 ? 3 : 5;
    const GAP       = 1; // 1px gap entre segmentos apilados

    function xBar(i) { return PAD_L + i * barWidth + barPad; }

    const handleMouseMove = (e) => {
        const svg = svgRef.current;
        if (!svg) return;
        const rect = svg.getBoundingClientRect();
        const relX = (e.clientX - rect.left) * (W / rect.width) - PAD_L;
        const idx  = Math.floor(relX / barWidth);
        if (idx < 0 || idx >= serie.length) { setTooltip(null); return; }
        setTooltip({ svgX: xBar(idx) + barActual / 2, idx });
    };

    return (
        <div style={{ position: "relative" }}>
            <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`}
                style={{ width: "100%", height: "auto", display: "block", userSelect: "none" }}
                onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>

                {/* Grid lines */}
                {Array.from({ length: gridLines + 1 }, (_, i) => {
                    const y   = PAD_T + (i / gridLines) * AREA_H;
                    const val = Math.round(maxTotal * (1 - i / gridLines));
                    return (
                        <g key={i}>
                            <line x1={PAD_L} y1={y} x2={W - PAD_R} y2={y} stroke="var(--color-track-bg)" strokeWidth="1" />
                            {val > 0 && <text x={PAD_L - 6} y={y + 4} textAnchor="end" fontSize="11" fill="var(--color-disabled-text)" fontFamily="system-ui,sans-serif">{val}</text>}
                        </g>
                    );
                })}

                {/* Barras apiladas */}
                {serie.map((d, i) => {
                    const sel      = tooltip?.idx === i;
                    const dayTotal = SERIES.reduce((s, k) => s + (d[k.key] || 0), 0);
                    let cumPx = 0; // acumula altura desde el fondo

                    return (
                        <g key={d.fecha} opacity={tooltip && !sel ? 0.45 : 1} style={{ transition: "opacity 0.1s" }}>
                            {SERIES.map(({ key, color }) => {
                                const val = d[key] || 0;
                                if (val === 0) return null;
                                const hPx = (val / maxTotal) * AREA_H;
                                const y   = PAD_T + AREA_H - cumPx - hPx;
                                cumPx    += hPx + GAP;
                                const isTop = key === [...SERIES].reverse().find(s => (d[s.key] || 0) > 0)?.key;
                                return (
                                    <rect key={key}
                                        x={xBar(i)} y={y}
                                        width={barActual} height={Math.max(hPx - GAP, 1)}
                                        rx={isTop ? 3 : 0} ry={isTop ? 3 : 0}
                                        fill={color} />
                                );
                            })}
                            {/* Highlight overlay en hover */}
                            {dayTotal > 0 && (
                                <rect x={xBar(i)} y={PAD_T + AREA_H - (dayTotal / maxTotal) * AREA_H}
                                    width={barActual} height={(dayTotal / maxTotal) * AREA_H}
                                    fill="transparent"
                                    stroke={sel ? "var(--color-track-bg)" : "none"} strokeWidth="1.5" rx="3" ry="3" />
                            )}
                        </g>
                    );
                })}

                {/* Etiquetas eje X */}
                {serie.map((d, i) => {
                    if (i % labelCada !== 0 && i !== serie.length - 1) return null;
                    return (
                        <text key={`lx-${i}`} x={xBar(i) + barActual / 2} y={H - 6}
                            textAnchor="middle" fontSize="10.5" fill="var(--color-disabled-text)" fontFamily="system-ui,sans-serif">
                            {fmtFecha(d.fecha)}
                        </text>
                    );
                })}

                {/* Crosshair */}
                {tooltip && (
                    <line x1={tooltip.svgX} y1={PAD_T} x2={tooltip.svgX} y2={PAD_T + AREA_H}
                        stroke="var(--color-track-bg)" strokeWidth="1" strokeDasharray="4 3" />
                )}
            </svg>

            {/* Tooltip HTML */}
            {tooltip && (() => {
                const d = serie[tooltip.idx];
                if (!d) return null;
                const total = SERIES.reduce((s, k) => s + (d[k.key] || 0), 0);
                return (
                    <div style={{
                        position: "absolute", top: 8,
                        left: `calc(${(tooltip.svgX / W) * 100}% + 8px)`,
                        transform: tooltip.svgX > W * 0.65 ? "translateX(calc(-100% - 16px))" : "none",
                        background: "var(--gris-900)", color: "var(--blanco)", borderRadius: 10,
                        padding: "8px 14px", fontSize: 12, pointerEvents: "none",
                        whiteSpace: "nowrap", zIndex: 10, boxShadow: "0 4px 16px rgba(var(--negro-rgb), 0.22)",
                        minWidth: 160,
                    }}>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>{fmtFechaCompleta(d.fecha)}</div>
                        {SERIES.map(({ key, label, color }) => {
                            const v = d[key] || 0;
                            return (
                                <div key={key} className="d-flex justify-content-between gap-3" style={{ marginBottom: 3 }}>
                                    <div className="d-flex align-items-center gap-2">
                                        <div style={{ width: 8, height: 8, borderRadius: 2, background: color, flexShrink: 0 }}></div>
                                        <span style={{ color: "rgba(var(--blanco-rgb), 0.7)" }}>{label}</span>
                                    </div>
                                    <span style={{ fontWeight: 700, color: v > 0 ? color : "rgba(var(--blanco-rgb), 0.3)" }}>{v}</span>
                                </div>
                            );
                        })}
                        <div style={{ borderTop: "1px solid rgba(var(--blanco-rgb), 0.12)", marginTop: 6, paddingTop: 5 }}>
                            <div className="d-flex justify-content-between">
                                <span style={{ color: "rgba(var(--blanco-rgb), 0.55)", fontSize: 11 }}>Total del día</span>
                                <span style={{ fontWeight: 700 }}>{total}</span>
                            </div>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
}

// ── Stat tile ─────────────────────────────────────────────────────────────────
function StatTile({ label, valor, color, icono, sub }) {
    return (
        <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4">
                <div className="mb-3">
                    <div className="rounded-3 d-flex align-items-center justify-content-center" style={{ width: 48, height: 48, background: `${color}18`, flexShrink: 0 }}>
                        <i className={`bi ${icono} fs-5`} style={{ color }}></i>
                    </div>
                </div>
                <p className="text-secondary fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>{label}</p>
                <h3 className="fw-bold text-dark mb-1">{valor}</h3>
                {sub && <p className="text-secondary mb-0" style={{ fontSize: "0.82rem" }}>{sub}</p>}
            </div>
        </div>
    );
}

// ── Barra horizontal ──────────────────────────────────────────────────────────
function FilaBarra({ label, total, pct, color }) {
    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-1">
                <span style={{ fontSize: "0.83rem", color: "var(--color-text-secondary)", fontWeight: 500 }}>{label}</span>
                <div className="d-flex align-items-center gap-2">
                    <span style={{ fontSize: "0.78rem", color: "var(--color-text-secondary)" }}>{total.toLocaleString("es-CO")}</span>
                    <span style={{ fontSize: "0.8rem", fontWeight: 700, color, minWidth: 36, textAlign: "right" }}>{pct}%</span>
                </div>
            </div>
            <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 7, overflow: "hidden" }}>
                <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.5s ease" }} />
            </div>
        </div>
    );
}

// ── Tabla de categorías con desglose por estado ───────────────────────────────
function TablaCategoriasEstados({ categorias }) {
    const [buscar,  setBuscar]  = useState("");
    const [pagina,  setPagina]  = useState(0);
    const POR_PAG = 12;

    const filtradas = categorias.filter(c =>
        !buscar || c.categoria.toLowerCase().includes(buscar.toLowerCase())
    );
    const visibles  = filtradas.slice(pagina * POR_PAG, (pagina + 1) * POR_PAG);
    const totalPags = Math.ceil(filtradas.length / POR_PAG);

    return (
        <div className="card border-0 shadow-sm rounded-4 mt-3">
            <div className="card-body p-4">

                {/* Cabecera */}
                <div className="d-flex flex-wrap align-items-start justify-content-between gap-3 mb-1">
                    <div>
                        <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.92rem" }}>
                            <i className="bi bi-grid-fill me-2" style={{ color: "var(--azul-logo-claro)" }}></i>
                            Estado de servicios por categoría
                        </h6>
                        <p className="text-secondary mb-0 mt-1" style={{ fontSize: "0.78rem" }}>
                            {filtradas.length} {filtradas.length === 1 ? "categoría" : "categorías"}
                            {buscar ? ` · filtrando "${buscar}"` : ""}
                        </p>
                    </div>
                    <input
                        type="text"
                        placeholder="Buscar categoría…"
                        value={buscar}
                        onChange={e => { setBuscar(e.target.value); setPagina(0); }}
                        className="form-control form-control-sm"
                        style={{ maxWidth: 210, fontSize: "0.8rem" }}
                    />
                </div>

                {/* Leyenda */}
                <div className="d-flex gap-3 flex-wrap mb-3 mt-3">
                    {SERIES.map(({ label, color }) => (
                        <div key={label} className="d-flex align-items-center gap-1">
                            <div style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }}></div>
                            <span style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)" }}>{label}</span>
                        </div>
                    ))}
                </div>

                {/* Filas */}
                {visibles.length > 0 ? (
                    <div className="d-flex flex-column gap-3">
                        {visibles.map(cat => (
                            <div key={cat.categoria}>
                                {/* Nombre + conteos por estado */}
                                <div className="d-flex justify-content-between align-items-center mb-1 flex-wrap gap-1">
                                    <span style={{ fontSize: "0.85rem", fontWeight: 600, color: "var(--color-text-primary)" }}>{cat.categoria}</span>
                                    <div className="d-flex align-items-center gap-2 flex-wrap">
                                        {SERIES.filter(s => cat[s.key] > 0).map(({ key, color }) => (
                                            <span key={key} className="rounded-pill px-2 py-0"
                                                style={{ fontSize: "0.72rem", fontWeight: 700, color, background: `${color}18`, border: `1px solid ${color}30` }}>
                                                {cat[key]}
                                            </span>
                                        ))}
                                        <span style={{ fontSize: "0.78rem", color: "var(--gris-400)" }}>
                                            {cat.total.toLocaleString("es-CO")} total
                                        </span>
                                    </div>
                                </div>
                                {/* Barra apilada horizontal */}
                                <div style={{ height: 8, borderRadius: 999, overflow: "hidden", display: "flex", gap: 1, background: "var(--color-track-bg)" }}>
                                    {SERIES.map(({ key, color }) => {
                                        const val = cat[key] || 0;
                                        if (val === 0) return null;
                                        const pct = (val / cat.total) * 100;
                                        return (
                                            <div key={key}
                                                style={{ width: `${pct}%`, background: color, height: "100%", minWidth: 2 }}
                                                title={`${key}: ${val}`} />
                                        );
                                    })}
                                </div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="text-secondary text-center py-4" style={{ fontSize: "0.85rem" }}>
                        <i className="bi bi-search me-1"></i>
                        No hay categorías que coincidan.
                    </div>
                )}

                {/* Paginación */}
                {totalPags > 1 && (
                    <div className="d-flex justify-content-between align-items-center mt-4 pt-2 border-top">
                        <span style={{ fontSize: "0.78rem", color: "var(--color-text-secondary)" }}>
                            {pagina * POR_PAG + 1}–{Math.min((pagina + 1) * POR_PAG, filtradas.length)} de {filtradas.length}
                        </span>
                        <div className="d-flex gap-1">
                            <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" style={{ fontSize: "0.75rem" }}
                                disabled={pagina === 0} onClick={() => setPagina(p => p - 1)}>
                                ‹ Anterior
                            </button>
                            <button className="btn btn-sm btn-outline-secondary rounded-pill px-3" style={{ fontSize: "0.75rem" }}
                                disabled={pagina + 1 >= totalPags} onClick={() => setPagina(p => p + 1)}>
                                Siguiente ›
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Servicios relacionados (EventoPropio click_servicio_relacionado) ──────────
// Bloques "Más servicios del vendedor" / "Servicios similares" al final de cada servicio
// (web serviciosRelacionados.jsx, app serviciosRelacionados.dart). Datos: data.relacionados de /reportes-servicios.
const BLOQUES_RELACIONADOS = [
    { key: "vendedor",  label: "Más servicios del vendedor", color: "var(--azul-logo-claro)" },
    { key: "similares", label: "Servicios similares",        color: "var(--violeta-mapa)"    },
];

function pctSeguro(parte, total) {
    if (!total) return 0;
    return Math.round((parte / total) * 1000) / 10; // 1 decimal
}

function PanelRelacionadosPlataforma({ titulo, icono, clics, vistas, clicsSesion, reservas }) {
    const max = Math.max(clics.vendedor, clics.similares, 1);
    return (
        <div className="card border-0 shadow-sm rounded-4 h-100">
            <div className="card-body p-4">
                <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "0.88rem" }}>
                    <i className={`bi ${icono} me-2`} style={{ color: "var(--azul-logo-claro)" }}></i>
                    {titulo}
                </h6>
                <p className="text-secondary mb-3" style={{ fontSize: "0.78rem" }}>
                    {clics.total.toLocaleString("es-CO")} clics · {pctSeguro(clics.total, vistas)}% de {vistas.toLocaleString("es-CO")} vistas de servicio
                </p>

                <div className="d-flex flex-column gap-3">
                    {BLOQUES_RELACIONADOS.map(({ key, label, color }) => {
                        const total = clics[key] || 0;
                        const pct   = Math.round((total / max) * 100);
                        return (
                            <div key={key}>
                                <div className="d-flex align-items-center justify-content-between mb-1">
                                    <span style={{ fontSize: "0.83rem", color: "var(--color-text-secondary)", fontWeight: 500 }}>{label}</span>
                                    <span style={{ fontSize: "0.83rem", fontWeight: 700, color }}>{total.toLocaleString("es-CO")}</span>
                                </div>
                                <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                                    <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.5s ease" }} />
                                </div>
                                <div style={{ fontSize: "0.72rem", color: "var(--gris-400)", marginTop: 3 }}>
                                    {(reservas[key] || 0).toLocaleString("es-CO")} reservas iniciadas tras el clic
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="border-top mt-3 pt-3" style={{ fontSize: "0.78rem", color: "var(--color-text-secondary)" }}>
                    <i className="bi bi-cart-check me-1" style={{ color: "var(--verde-esmeralda)" }}></i>
                    <strong>{reservas.total.toLocaleString("es-CO")}</strong> reservas iniciadas de {clicsSesion.toLocaleString("es-CO")} clics con sesión
                    {" "}(<strong>{pctSeguro(reservas.total, clicsSesion)}%</strong>)
                </div>
            </div>
        </div>
    );
}

function SeccionServiciosRelacionados({ relacionados, dias }) {
    const r = relacionados || {};
    const vacio = { total: 0, vendedor: 0, similares: 0 };
    const clics       = { web: r.clics?.web || vacio, app: r.clics?.app || vacio };
    const vistas      = { web: r.vistas?.web || 0, app: r.vistas?.app || 0 };
    const clicsSesion = { web: r.clicsSesion?.web || 0, app: r.clicsSesion?.app || 0 };
    const reservas    = { web: r.reservasTrasClic?.web || vacio, app: r.reservasTrasClic?.app || vacio };
    const posiciones  = Array.isArray(r.posiciones) ? r.posiciones : [];
    const topDestinos = Array.isArray(r.topDestinos) ? r.topDestinos : [];

    const totalClics    = clics.web.total + clics.app.total;
    const totalVistas   = vistas.web + vistas.app;
    const totalSesion   = clicsSesion.web + clicsSesion.app;
    const totalReservas = reservas.web.total + reservas.app.total;
    const maxPos        = Math.max(...posiciones.map(p => p.web + p.app), 1);
    const maxDestino    = topDestinos[0]?.total || 1;

    return (
        <div className="mt-4">
            <div className="mb-3">
                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.92rem" }}>
                    <i className="bi bi-collection-fill me-2" style={{ color: "var(--azul-logo-claro)" }}></i>
                    Servicios relacionados — últimos {dias} días
                </h6>
                <p className="text-secondary mb-0 mt-1" style={{ fontSize: "0.78rem" }}>
                    Clics en "Más servicios del vendedor" y "Servicios similares" al final de cada servicio. Mide si recuperan compradores o solo distraen.
                </p>
            </div>

            {/* Tiles */}
            <div className="row g-3 mb-3">
                <div className="col-6 col-md-3">
                    <StatTile label="Clics totales" valor={totalClics.toLocaleString("es-CO")} color="var(--azul-logo-claro)" icono="bi-hand-index-thumb-fill"
                        sub={`Web ${clics.web.total.toLocaleString("es-CO")} · App ${clics.app.total.toLocaleString("es-CO")}`} />
                </div>
                <div className="col-6 col-md-3">
                    <StatTile label="Tasa de clic" valor={`${pctSeguro(totalClics, totalVistas)}%`} color="var(--violeta-mapa)" icono="bi-percent"
                        sub={`Sobre ${totalVistas.toLocaleString("es-CO")} vistas de servicio`} />
                </div>
                <div className="col-6 col-md-3">
                    <StatTile label="Reservas iniciadas tras el clic" valor={totalReservas.toLocaleString("es-CO")} color="var(--verde-esmeralda)" icono="bi-cart-check-fill"
                        sub="Mismo usuario, mismo servicio, ≤ 24 h" />
                </div>
                <div className="col-6 col-md-3">
                    <StatTile label="Conversión del clic" valor={`${pctSeguro(totalReservas, totalSesion)}%`} color="var(--ambar-600)" icono="bi-graph-up-arrow"
                        sub={`De ${totalSesion.toLocaleString("es-CO")} clics con sesión iniciada`} />
                </div>
            </div>

            {/* Web / App por bloque */}
            <div className="row g-3">
                <div className="col-12 col-md-6">
                    <PanelRelacionadosPlataforma titulo="Web" icono="bi-globe2" clics={clics.web} vistas={vistas.web} clicsSesion={clicsSesion.web} reservas={reservas.web} />
                </div>
                <div className="col-12 col-md-6">
                    <PanelRelacionadosPlataforma titulo="App" icono="bi-phone-fill" clics={clics.app} vistas={vistas.app} clicsSesion={clicsSesion.app} reservas={reservas.app} />
                </div>
            </div>

            {/* Posición + top destinos */}
            <div className="row g-3 mt-0">
                <div className="col-12 col-lg-5">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                        <div className="card-body p-4">
                            <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "0.88rem" }}>
                                <i className="bi bi-list-ol me-2" style={{ color: "var(--ambar-600)" }}></i>
                                Clics por posición en el slider
                            </h6>
                            <p className="text-secondary mb-3" style={{ fontSize: "0.78rem" }}>
                                Si casi todo se concentra en la primera tarjeta, el orden importa más que la cantidad.
                            </p>
                            {posiciones.length > 0 ? (
                                <div className="d-flex flex-column gap-3">
                                    {posiciones.map(({ posicion, web, app }) => {
                                        const total = web + app;
                                        return (
                                            <div key={posicion}>
                                                <div className="d-flex align-items-center justify-content-between mb-1">
                                                    <span style={{ fontSize: "0.83rem", color: "var(--color-text-secondary)" }}>Tarjeta {posicion + 1}</span>
                                                    <span style={{ fontSize: "0.78rem", color: "var(--color-text-secondary)" }}>
                                                        Web {web.toLocaleString("es-CO")} · App {app.toLocaleString("es-CO")} · <strong style={{ color: "var(--ambar-600)" }}>{total.toLocaleString("es-CO")}</strong>
                                                    </span>
                                                </div>
                                                <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                                                    <div style={{ width: `${Math.round((total / maxPos) * 100)}%`, height: "100%", background: "var(--ambar-600)", borderRadius: 999, transition: "width 0.5s ease" }} />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <div className="text-secondary text-center py-3" style={{ fontSize: "0.85rem" }}>
                                    <i className="bi bi-info-circle me-1"></i>Sin clics en este periodo.
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                <div className="col-12 col-lg-7">
                    <div className="card border-0 shadow-sm rounded-4 h-100">
                        <div className="card-body p-4">
                            <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "0.88rem" }}>
                                <i className="bi bi-star-fill me-2" style={{ color: "var(--verde-esmeralda)" }}></i>
                                Servicios más abiertos desde los bloques
                            </h6>
                            <p className="text-secondary mb-3" style={{ fontSize: "0.78rem" }}>
                                Top 5 servicios que la gente abrió desde un servicio relacionado (web + app).
                            </p>
                            {topDestinos.length > 0 ? (
                                <div className="d-flex flex-column gap-3">
                                    {topDestinos.map((t) => (
                                        <div key={t.idServicio}>
                                            <div className="d-flex align-items-center justify-content-between mb-1 gap-2">
                                                <span className="text-truncate" style={{ fontSize: "0.83rem", color: "var(--color-text-secondary)" }} title={t.nombre}>{t.nombre}</span>
                                                <span style={{ fontSize: "0.83rem", fontWeight: 700, color: "var(--verde-esmeralda)", flexShrink: 0 }}>{t.total.toLocaleString("es-CO")}</span>
                                            </div>
                                            <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                                                <div style={{ width: `${Math.round((t.total / maxDestino) * 100)}%`, height: "100%", background: "var(--verde-esmeralda)", borderRadius: 999, transition: "width 0.5s ease" }} />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-secondary text-center py-3" style={{ fontSize: "0.85rem" }}>
                                    <i className="bi bi-info-circle me-1"></i>Sin clics en este periodo.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <p className="text-secondary mt-2 mb-0" style={{ fontSize: "0.72rem" }}>
                <i className="bi bi-info-circle me-1"></i>
                "Reservas iniciadas tras el clic" solo puede medirse con usuarios con sesión iniciada (los invitados no envían su id en los eventos propios), por eso la conversión se calcula sobre los clics con sesión.
            </p>
        </div>
    );
}

// ── Componente principal ──────────────────────────────────────────────────────
function ReportesPorServicios() {
    const [dias,     setDias]     = useState(30);
    const [data,     setData]     = useState(null);
    const [cargando, setCargando] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                setCargando(true);
                const res = await axios.post("/reportes-servicios", { params: { dias } });
                if (res.data.exito) setData(res.data);
            } catch { setData(null); }
            finally { setCargando(false); }
        })();
    }, [dias]);

    const serie                = data?.serie                || [];
    const totales              = data?.totales              || {};
    const topCategorias        = data?.topCategorias        || [];
    const categoriasPorEstado  = data?.categoriasPorEstado  || [];
    const eventosFunnelWeb     = data?.eventosFunnelWeb     || [];
    const eventosFunnelApp     = data?.eventosFunnelApp     || [];
    const pasosPorNumeroWeb    = data?.pasosPorNumeroWeb    || [];
    const pasosPorNumeroApp    = data?.pasosPorNumeroApp    || [];
    const pasosPorNumero       = data?.pasosPorNumero       || [];
    const totalServicios = Object.values(totales).reduce((s, v) => s + v, 0);
    const maxCat         = topCategorias[0]?.total || 1;

    return (
        <div className="container-fluid">

            {/* Encabezado */}
            <div className="d-flex justify-content-between flex-wrap align-items-center gap-3 pt-3 pb-2 mb-4 border-bottom">
                <div>
                    <h1 className="h2 text-dark mb-1">Reportes · Servicios</h1>
                    <p className="text-secondary mb-0 small">Nuevos servicios creados por día, desglosados por estado.</p>
                </div>
                <div className="d-flex gap-1">
                    {DIAS_OPS.map(op => (
                        <button key={op.key} type="button"
                            className={`btn btn-sm rounded-pill px-3 ${dias === op.key ? "btn-dark" : "btn-outline-secondary"}`}
                            style={{ fontSize: "0.78rem" }}
                            onClick={() => setDias(op.key)}>
                            {op.label}
                        </button>
                    ))}
                </div>
            </div>

            {cargando ? (
                <div className="text-center py-5">
                    <span className="spinner-border text-primary" role="status" />
                </div>
            ) : (
                <>
                    {/* ── Stat tiles por estado ── */}
                    <div className="row g-3 mb-4">
                        {[
                            { key: "Aprobado",    label: "Aprobados",    color: "var(--verde-esmeralda)", icono: "bi-check-circle-fill" },
                            { key: "Borrador",    label: "Borradores",   color: "var(--ambar-600)", icono: "bi-pencil-fill"        },
                            { key: "Pausado",     label: "Pausados",     color: "var(--color-text-secondary)", icono: "bi-pause-circle-fill"  },
                            { key: "Desaprobado", label: "Desaprobados", color: "var(--rojo-600)", icono: "bi-x-circle-fill"      },
                        ].map(({ key, label, color, icono }) => (
                            <div className="col-6 col-md-3" key={key}>
                                <StatTile label={label} valor={(totales[key] || 0).toLocaleString("es-CO")} color={color} icono={icono} />
                            </div>
                        ))}
                    </div>

                    {/* ── Gráfica apilada ── */}
                    <div className="card border-0 shadow-sm rounded-4 mb-4">
                        <div className="card-body p-4">
                            {/* Leyenda */}
                            <div className="d-flex flex-wrap align-items-center gap-4 mb-4">
                                <span className="fw-semibold text-dark" style={{ fontSize: "0.92rem" }}>
                                    Servicios creados por día — últimos {dias} días
                                </span>
                                <div className="d-flex gap-3 flex-wrap ms-auto">
                                    {SERIES.map(({ label, color }) => (
                                        <div key={label} className="d-flex align-items-center gap-1">
                                            <div style={{ width: 10, height: 10, borderRadius: 2, background: color }}></div>
                                            <span style={{ fontSize: "0.75rem", color: "var(--color-text-secondary)" }}>{label}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {serie.length > 0
                                ? <StackedBarChart serie={serie} dias={dias} />
                                : <div className="text-center py-5 text-secondary"><i className="bi bi-bar-chart-stacked fs-2 d-block mb-2"></i>Sin datos.</div>
                            }
                        </div>
                    </div>

                    {/* ── Distribución actual + Top categorías ── */}
                    <div className="row g-3">

                        {/* Distribución por estado */}
                        <div className="col-12 col-lg-5">
                            <div className="card border-0 shadow-sm rounded-4 h-100">
                                <div className="card-body p-4">
                                    <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "0.92rem" }}>
                                        <i className="bi bi-pie-chart-fill me-2" style={{ color: "var(--violeta-mapa)" }}></i>
                                        Distribución actual
                                    </h6>
                                    <p className="text-secondary mb-3" style={{ fontSize: "0.78rem" }}>
                                        Sobre {totalServicios.toLocaleString("es-CO")} servicios en total
                                    </p>
                                    <div className="d-flex flex-column gap-3">
                                        {SERIES.map(({ key, label, color }) => {
                                            const total = totales[key] || 0;
                                            const pct   = totalServicios ? Math.round((total / totalServicios) * 100) : 0;
                                            return <FilaBarra key={key} label={label} total={total} pct={pct} color={color} />;
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Top categorías */}
                        <div className="col-12 col-lg-7">
                            <div className="card border-0 shadow-sm rounded-4 h-100">
                                <div className="card-body p-4">
                                    <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "0.92rem" }}>
                                        <i className="bi bi-grid-fill me-2" style={{ color: "var(--verde-esmeralda)" }}></i>
                                        Top categorías (servicios aprobados)
                                    </h6>
                                    <p className="text-secondary mb-3" style={{ fontSize: "0.78rem" }}>
                                        Categorías con más servicios publicados
                                    </p>
                                    {topCategorias.length > 0 ? (
                                        <div className="d-flex flex-column gap-3">
                                            {topCategorias.map((cat, i) => {
                                                const pct   = Math.round((cat.total / maxCat) * 100);
                                                const color = ["var(--verde-esmeralda)","var(--azul-logo-claro)","var(--violeta-mapa)","var(--ambar-600)","var(--rojo-600)","var(--cian-600)","var(--verde-lima-600)","var(--naranja-600)"][i % 8];
                                                return (
                                                    <div key={cat.categoria}>
                                                        <div className="d-flex justify-content-between align-items-center mb-1">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, flexShrink: 0 }}></div>
                                                                <span style={{ fontSize: "0.83rem", color: "var(--color-text-secondary)" }}>{cat.categoria}</span>
                                                            </div>
                                                            <span style={{ fontSize: "0.83rem", fontWeight: 700, color }}>{cat.total.toLocaleString("es-CO")}</span>
                                                        </div>
                                                        <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                                                            <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.5s ease" }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-secondary text-center py-3" style={{ fontSize: "0.85rem" }}>Sin datos de categorías.</div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* ── Categorías × estado ── */}
                    {categoriasPorEstado.length > 0 && (
                        <TablaCategoriasEstados categorias={categoriasPorEstado} />
                    )}

                    {/* ── Funnel de creación de servicios (EventoPropio) ── */}
                    {(() => {
                        const CONFIG_FUNNEL = {
                            iniciar_creacion_servicio:   { label: "Iniciaron creación",   desc: "Vendedores que abrieron el formulario de crear servicio por primera vez.",         color: "var(--azul-logo-claro)", icono: "bi-play-circle"       },
                            paso_creacion_servicio:      { label: "Completaron un paso",  desc: "Pasos del wizard detectados como llenos (checklist en verde), una sola vez cada uno — sin importar en qué dirección se movió.", color: "var(--violeta-mapa)", icono: "bi-arrow-right-circle" },
                            poner_en_revision:           { label: "Pusieron en revisión", desc: "Servicios enviados a revisión para ser aprobados y publicados.",                   color: "var(--ambar-600)", icono: "bi-send"               },
                            abandonar_creacion_servicio: { label: "Abandonaron",          desc: "Sesiones donde el vendedor cerró o salió sin completar el servicio.",              color: "var(--rojo-600)", icono: "bi-x-circle"           },
                        };

                        function PanelFunnelVendedor({ titulo, icono, funnel }) {
                            const max = Math.max(...funnel.map(e => e.total), 1);
                            return (
                                <div className="card border-0 shadow-sm rounded-4 h-100">
                                    <div className="card-body p-4">
                                        <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "0.88rem" }}>
                                            <i className={`bi ${icono} me-2`} style={{ color: "var(--violeta-mapa)" }}></i>
                                            {titulo}
                                        </h6>
                                        <p className="text-secondary mb-3" style={{ fontSize: "0.78rem" }}>
                                            Eventos registrados en el proceso de publicación
                                        </p>
                                        <div className="d-flex flex-column gap-3">
                                            {funnel.map(({ tipo, total }) => {
                                                const cfg = CONFIG_FUNNEL[tipo] || { label: tipo, desc: "", color: "var(--color-text-secondary)", icono: "bi-dot" };
                                                const pct = Math.round((total / max) * 100);
                                                return (
                                                    <div key={tipo}>
                                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                                            <div className="d-flex align-items-center gap-2">
                                                                <i className={`bi ${cfg.icono}`} style={{ color: cfg.color, fontSize: 13 }}></i>
                                                                <div>
                                                                    <div style={{ fontSize: "0.83rem", color: "var(--color-text-secondary)", fontWeight: 500 }}>{cfg.label}</div>
                                                                    {cfg.desc && <div style={{ fontSize: "0.72rem", color: "var(--gris-400)", lineHeight: 1.3 }}>{cfg.desc}</div>}
                                                                </div>
                                                            </div>
                                                            <span style={{ fontSize: "0.83rem", fontWeight: 700, color: cfg.color, flexShrink: 0, marginLeft: 8 }}>{total.toLocaleString("es-CO")}</span>
                                                        </div>
                                                        <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                                                            <div style={{ width: `${pct}%`, height: "100%", background: cfg.color, borderRadius: 999, transition: "width 0.5s ease" }} />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div className="mt-4">
                                <div className="mb-3">
                                    <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.92rem" }}>
                                        <i className="bi bi-lightning-charge-fill me-2" style={{ color: "var(--violeta-mapa)" }}></i>
                                        Funnel de creación — últimos 30 días
                                    </h6>
                                    <p className="text-secondary mb-0 mt-1" style={{ fontSize: "0.78rem" }}>
                                        Eventos registrados en el proceso de publicación de servicios.
                                    </p>
                                </div>
                                <div className="row g-3">
                                    <div className="col-12 col-md-6">
                                        <PanelFunnelVendedor titulo="Web" icono="bi-globe2" funnel={eventosFunnelWeb} />
                                    </div>
                                    <div className="col-12 col-md-6">
                                        <PanelFunnelVendedor titulo="App" icono="bi-phone-fill" funnel={eventosFunnelApp} />
                                    </div>
                                </div>

                                {/* Desglose por paso — Web y App */}
                                {(pasosPorNumeroWeb.length > 0 || pasosPorNumeroApp.length > 0) && (() => {
                                    const PASO_LABEL = {
                                        1: "Paso 1 · Tipo de servicio",
                                        2: "Paso 2 · Info y descripción",
                                        3: "Paso 3 · Imágenes",
                                        4: "Paso 4 · Paquetes y precios",
                                        5: "Paso 5 · Preguntas frecuentes",
                                    };
                                    const PASO_COLORS = ["var(--azul-logo-claro)","var(--violeta-mapa)","var(--verde-esmeralda)","var(--ambar-600)","var(--rojo-600)"];

                                    function PanelPasos({ titulo, icono, pasos }) {
                                        const max = Math.max(...pasos.map(p => p.total), 1);
                                        return (
                                            <div className="card border-0 shadow-sm rounded-4 h-100">
                                                <div className="card-body p-4">
                                                    <h6 className="fw-bold text-dark mb-1" style={{ fontSize: "0.88rem" }}>
                                                        <i className={`bi ${icono} me-2`} style={{ color: "var(--ambar-600)" }}></i>
                                                        {titulo}
                                                    </h6>
                                                    <p className="text-secondary mb-3" style={{ fontSize: "0.78rem" }}>
                                                        Cuántas veces se completó cada paso (últimos 30 días)
                                                    </p>
                                                    {pasos.length > 0 ? (
                                                        <div className="d-flex flex-column gap-3">
                                                            {pasos.map(({ paso, total }) => {
                                                                const pct   = Math.round((total / max) * 100);
                                                                const color = PASO_COLORS[Number(paso) - 1] || "var(--texto-gris-oscuro)";
                                                                return (
                                                                    <div key={paso}>
                                                                        <div className="d-flex align-items-center justify-content-between mb-1">
                                                                            <span style={{ fontSize: "0.83rem", color: "var(--color-text-secondary)" }}>
                                                                                {PASO_LABEL[paso] || `Paso ${paso}`}
                                                                            </span>
                                                                            <span style={{ fontSize: "0.83rem", fontWeight: 700, color }}>{total.toLocaleString("es-CO")}</span>
                                                                        </div>
                                                                        <div style={{ background: "var(--color-track-bg)", borderRadius: 999, height: 6, overflow: "hidden" }}>
                                                                            <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 999, transition: "width 0.5s ease" }} />
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    ) : (
                                                        <div className="text-secondary text-center py-3" style={{ fontSize: "0.85rem" }}>
                                                            <i className="bi bi-info-circle me-1"></i>Sin datos.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        );
                                    }

                                    return (
                                        <div className="mt-3">
                                            <div className="mb-3">
                                                <h6 className="fw-bold text-dark mb-0" style={{ fontSize: "0.92rem" }}>
                                                    <i className="bi bi-list-ol me-2" style={{ color: "var(--ambar-600)" }}></i>
                                                    ¿En qué paso se quedan?
                                                </h6>
                                                <p className="text-secondary mb-0 mt-1" style={{ fontSize: "0.78rem" }}>
                                                    Cuántas veces se completó cada paso, en orden — si el número baja fuerte en un paso, ahí se están frenando (últimos 30 días).
                                                </p>
                                            </div>
                                            <div className="row g-3">
                                                <div className="col-12 col-md-6">
                                                    <PanelPasos titulo="Web" icono="bi-globe2" pasos={pasosPorNumeroWeb} />
                                                </div>
                                                <div className="col-12 col-md-6">
                                                    <PanelPasos titulo="App" icono="bi-phone-fill" pasos={pasosPorNumeroApp} />
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        );
                    })()}

                    {/* ── Servicios relacionados (EventoPropio click_servicio_relacionado) ── */}
                    <SeccionServiciosRelacionados relacionados={data?.relacionados} dias={dias} />
                </>
            )}
        </div>
    );
}

export default ReportesPorServicios;
